<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { watch, computed, ref, onBeforeUnmount } from "vue"
import { useRoute } from "vue-router"
import { ConfigGraphCard, ConditionBlock } from "@types"
import {
    DDA_STATUS,
    SOURCES_TYPES,
    SOURCE_WITHOUT_RAW_FREQ,
    SOURCE_WITHOUT_HOURLY_FREQ,
    TASK_REBUILD_SOURCE_DATA,
} from "@core/constants"
import { useGraphStore } from "@modules/data/stores/graph.store"
import { useSourceStore } from "@core/stores/source.store"
import { useAuthStore } from "@core/stores/auth.store"
import { useTaskStore } from "@core/stores/task.store"
import { SourceService } from "@core/services"
import { Freq } from "@types"
import { useFetchWrapper, useKPI } from "@core/composables"
import { formatIdSource } from "@core/helpers"
import {
    handleSourceCondition,
    handleUserCondition,
} from "@modules/data/helpers"
import { useI18n } from "vue-i18n"
import {
    evaluateVisibleConditionsBlocks,
    VISIBLE_CONDITIONS_COMBINE_CARD_DEFAULT,
} from "@modules/data/utils/evaluateVisibleConditionsBlocks"
import SourceDataGrdfView, {
    type DataSourceView,
} from "@modules/data/components/data/grdf/SourceDataGrdfView.vue"
import { fetchSourceDataGrdf } from "@modules/data/services/sourceDataGrdf.service"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: ({ named }: { named: (key: string) => string }) => {
                const typeMapping = {
                    pdl: "du compteur",
                    pce: "du compteur",
                    idopenmeteo: "de la station météo",
                    idmeteofrance: "de la station météo",
                    cpcu: "du compteur",
                    dalkia: "du compteur",
                    eaubordeaux: "du compteur",
                } as Record<string, string>

                return `Données ${typeMapping[named("idtype")] ?? "du capteur"}`
            },
            export: "Exporter les données",
            reload: "Recharger les données",
            empty: "Veuillez sélectionner un graphique pour afficher les données.",
            all: "Ajouter tous les graphiques",
            pce_freq_missing:
                'Les données du compteur ne sont pas encore disponibles. Cliquer sur le bouton "Recharger les données". pour obtenir les dernières données.',
            not_exist: `Enedis nous a remonté que le compteur électrique <strong>{idsource}</strong> n'existe pas sur le réseau. Nous vous invitons à contacter Enedis au <a href="tel:09 70 83 19 70"><strong class="strong--small">09 70 83 19 70</strong></a> et dire "Je souhaite parler à un conseiller." pour comprendre la situation.`,
            consent_request:
                "Une demande de consentement est en attente pour ce compteur.",
            grdf_error:
                "Impossible de charger les données du compteur sur cette période.",
            cdc_activating:
                "La courbe de charge de ce compteur est en cours d'activation chez Enedis. Les premières mesures peuvent mettre jusqu'à 3 jours à remonter — c'est normal de ne pas encore voir de données.",
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const graphStore = useGraphStore()
const sourceStore = useSourceStore()
const taskStore = useTaskStore()
const authStore = useAuthStore()
const sourceService = new SourceService()
const route = useRoute()

const { fetch: fetchInitialData, isFetching: isFetchingInitialData } =
    useFetchWrapper(async (id: string) => {
        graphStore.setCurrentSourceId(id)
        if (!graphStore.isGraphListLoaded) {
            await graphStore.fetchGraphList()
        }
    })

// ================================
// COMPUTED
// ================================
/** Id de la source depuis la route (string ou premier élément si tableau). */
const sourceIdParam = computed(() => {
    const p = route.params._id_source
    return Array.isArray(p) ? p[0] : p
})

/** Source courante chargée depuis le store (réactive après fetchSourceById). */
const currentSource = computed(() => {
    const id = sourceIdParam.value
    return id ? sourceStore.getSourceById(id) : undefined
})

// ================================
// GRDF gaz (PCE) — page de données brutes dédiée (DEV-595)
// ================================
/** Source = compteur gaz GRDF (PCE) → vue brute dédiée (Facturation/Suivi).
 *  On teste `idtype` ET `type` : dans le store la source est fusionnée
 *  `{...sourceDapi, ...sourceLegacy}` et le `type` legacy (souvent null) écrase
 *  le `type` calculé par dapi → `idtype` ("pce") est le champ fiable. */
const isGrdfGas = computed(() => {
    const s = currentSource.value
    return s?.idtype === SOURCES_TYPES.PCE || s?.type === SOURCES_TYPES.PCE
})

/** Date → YYYY-MM-DD (local, sans décalage TZ). */
function toYmd(d: Date): string {
    const m = String(d.getMonth() + 1).padStart(2, "0")
    const day = String(d.getDate()).padStart(2, "0")
    return `${d.getFullYear()}-${m}-${day}`
}
const grdfDateFrom = computed(() => toYmd(graphStore.period[0]))
const grdfDateTo = computed(() => toYmd(graphStore.period[1]))

const grdfSources = ref<DataSourceView[]>([])
const grdfLoading = ref(false)
const grdfError = ref(false)

/** Charge les 2 vues GRDF (Facturation / Suivi) via la route dapi `/datav2/series`. */
async function loadGrdf(): Promise<void> {
    const idSource = currentSource.value?._id_source
    if (!isGrdfGas.value || !idSource) return
    grdfLoading.value = true
    grdfError.value = false
    try {
        grdfSources.value = await fetchSourceDataGrdf(
            idSource,
            grdfDateFrom.value,
            grdfDateTo.value
        )
    } catch {
        // On NE vide PAS `grdfSources` : on garde les données précédentes
        // affichées (le composant reste monté) en cas d'échec d'un refetch.
        grdfError.value = true
    } finally {
        grdfLoading.value = false
    }
}

/** Sélection de période libre (calendrier range) → recadre la fenêtre → recharge. */
function onGrdfPeriod(period: [Date, Date]): void {
    graphStore.period = period
}

// (re)charge dès que la source GRDF ou la fenêtre change
watch(
    [isGrdfGas, () => currentSource.value?._id_source, () => graphStore.period],
    () => {
        if (isGrdfGas.value) void loadGrdf()
    },
    { immediate: true }
)

/**
 * Récupère la liste des graphiques de configuration en fonction du type de source.
 */
const getGraphConfigList = computed<Record<string, ConfigGraphCard> | null>(
    () => {
        if (!currentSource.value?.type) return null
        switch (currentSource.value?.type) {
            case SOURCES_TYPES.PDL:
                return graphStore.getGraphConfigByCategory("GRAPH_PDL")

            case SOURCES_TYPES.ELECSAVOIE:
                return graphStore.getGraphConfigByCategory("GRAPH_ELECSAVOIE")
            case SOURCES_TYPES.PCE:
                return graphStore.getGraphConfigByCategory("GRAPH_PCE")

            case SOURCES_TYPES.REGAZ:
                return graphStore.getGraphConfigByCategory("GRAPH_REGAZ")

            case SOURCES_TYPES.CPCU:
            case SOURCES_TYPES.DALKIA:
                return graphStore.getGraphConfigByCategory("GRAPH_RCU")
            case SOURCES_TYPES.EAU_BORDEAUX:
                return graphStore.getGraphConfigByCategory("GRAPH_EAU")
            case SOURCES_TYPES.METEOFRANCE:
            case SOURCES_TYPES.OPENMETEO:
            case SOURCES_TYPES.METEO:
                return graphStore.getGraphConfigByCategory(
                    "GRAPH_WEATHER_STATION"
                )

            default:
                return graphStore.getGraphConfigByCategory("GRAPH_IOT")
        }
    }
)

/**
 * Liste des cartes de graphiques disponibles en appliquant les conditions de visibilité.
 */
const getGraphCardListAvailable = computed(() => {
    return (
        Object.values(getGraphConfigList.value ?? [])
            // Filtre les cartes de graphiques en fonction des conditions de visibilité
            .filter((graphCard) => {
                // si un paramétre graph à été passé en query on affiche ceux passé en paramétres
                if (graphStore?.getGraphQuery?.length > 0) {
                    return graphStore.getGraphQuery.includes(graphCard.id)
                }

                if (graphCard.visible_conditions?.length === 0) return true
                return evaluateVisibleConditionsBlocks(
                    graphCard.visible_conditions,
                    graphCard.visible_conditions_combine,
                    VISIBLE_CONDITIONS_COMBINE_CARD_DEFAULT,
                    (condition: ConditionBlock) => {
                        if (condition.origin === "user") {
                            return handleUserCondition(condition)
                        }
                        if (!currentSource.value) return false
                        return handleSourceCondition(
                            [currentSource.value],
                            condition
                        )
                    }
                )
            })
            // Trie les graphiques dont au moins 1 des variables des datasets est présente dans le champ _variable de la source
            .filter((graphCard) => {
                // Récupère les variables dans le champ dataset
                const variables = graphCard.graph_list
                    .flatMap((dataset) => {
                        return dataset.datasets.map((dataset) => {
                            return dataset.type?.split(".")[0] ?? ""
                        })
                    })
                    .filter((variable) => variable !== "") as string[]

                // Vérifie si au moins une des variables est présente dans le champ _variable de la source
                const sourceVariables = sourceStore.getVariablesBySourceId(
                    currentSource.value?._id_source ?? ""
                )
                return (
                    sourceVariables.filter((type: string) =>
                        variables.includes(type)
                    ).length > 0
                )
            })
            // Map pour ajouter la liste des graphiques à cacher
            .map((graphCard) => {
                return {
                    ...graphCard,
                    // Liste des graphiques à cacher car pas de variables disponibles dans le champ _variable de la source
                    graphHidden: graphCard.graph_list
                        .filter((graphItem) => {
                            // Récupère les variables dans le champ dataset
                            const variables = graphItem.datasets
                                .map((dataset) => {
                                    return dataset.type?.split(".")[0] ?? ""
                                })
                                .filter(
                                    (variable) => variable !== ""
                                ) as string[]
                            const sourceVariables =
                                sourceStore.getVariablesBySourceId(
                                    currentSource.value?._id_source ?? ""
                                )
                            // Vérifie si au moins une des variables est présente dans le champ _variable de la source
                            return (
                                sourceVariables.filter((type: string) =>
                                    variables.includes(type)
                                ).length === 0 &&
                                !graphItem.id?.includes("cout")
                            )
                        })
                        .map((graphItem) => {
                            return graphItem.id
                        }),
                }
            })
            .map((graphCard) => {
                return {
                    graphId: graphCard.id,
                    graphHidden: graphCard.graphHidden,
                }
            })
    )
})

/**
 * Liste des graphiques sélectionnés dans le sélecteur multiple.
 */
const getGraphSelected = computed(() => {
    return getGraphCardListAvailable.value.filter((graph) =>
        graphStore.graphs.includes(graph.graphId)
    )
})

/**
 * Récupère la liste des options de carte de graphiques disponibles
 */
const getCardGraphOptionList = computed(() => {
    return getGraphCardListAvailable.value.map((graph) => graph.graphId)
})

/**
 * Récupère la liste des fréquences disponibles selon le type de source
 * Exclut "raw" pour les sources CPCU et REGAZ
 */
const getAvailableFreqs = computed(() => {
    let allFreqs = ["raw", "hourly", "daily", "weekly", "monthly"]

    // Si la source n'est pas encore chargée on affiche toutes les fréquences
    if (!currentSource.value?.type) {
        return allFreqs
    }

    // Cas particulier pour les sources qui n'ont pas de fréquence raw
    if (SOURCE_WITHOUT_RAW_FREQ.includes(currentSource.value.type)) {
        allFreqs = allFreqs.filter((freq) => freq !== "raw")
    }
    // Cas particulier pour les sources qui n'ont pas de fréquence horaire
    if (SOURCE_WITHOUT_HOURLY_FREQ.includes(currentSource.value.type)) {
        allFreqs = allFreqs.filter((freq) => freq !== "hourly")
    }
    return allFreqs
})

/**
 * Message global affiché en haut de la page.
 * - si le compteur n'existe pas
 */
const getGlobalMessage = computed<{
    message: string
    severity: string
    closable?: boolean
    action?: { fn: () => void; message: string; severity?: string }
    position: string
} | null>(() => {
    const source = currentSource.value
    if (
        [SOURCES_TYPES.PDL, SOURCES_TYPES.PCE].includes(
            source?.type as string
        ) &&
        source?.consentrequest
    ) {
        return {
            message: t("consent_request", {
                idsource: formatIdSource(
                    source?.idsource as string,
                    source?.type as string
                ),
            }),
            severity: "warn",
            position: "bottom",
        }
    } else if (
        source?.type === SOURCES_TYPES.PDL &&
        source?.informations?.pdl?.etat !== "en_service" &&
        !source?.consentrequest
    ) {
        return {
            message: t("not_exist", {
                idsource: formatIdSource(
                    source?.idsource as string,
                    source?.type as string
                ),
            }),
            severity: "error",
            position: "bottom",
        }
    } else if (
        source?.type === SOURCES_TYPES.PDL &&
        source?.informations?.pdl?.data_retrieval?.historical?.state ===
            "delayed"
    ) {
        return {
            message: t("cdc_activating"),
            severity: "warn",
            closable: true,
            position: "top",
        }
    } else if (getGraphCardListAvailable.value.length === 0) {
        return {
            message: t("empty"),
            severity: "info",
            action: {
                fn: onClickAddOptimalGraphs,
                message: t("all"),
            },
            position: "bottom",
        }
    }

    // Vérifie si la source est un compteur de gaz avec un état de droit d'accès Active mais sans fréquence dans les données contractuelles
    else if (
        source?.type === SOURCES_TYPES.PCE &&
        source?.informations?.pce?.droit_access?.etat === DDA_STATUS.ACTIVE &&
        !source?.informations?.pce?.compteur?.frequence
    ) {
        return {
            message: t("pce_freq_missing"),
            severity: "warn",
            closable: true,
            action: {
                fn: onClickReload,
                message: t("reload"),
                severity: "warn",
            },
            position: "top",
        }
    }

    return null
})

// ================================
// COMPOSABLES (KPI)
// ================================
const {
    indicators,
    onClickKPI,
    fetchKPIValue,
    loading: loadingKPI,
    error: errorKPI,
} = useKPI("source", getGraphConfigList)

/** Titre de la page pour le header (compteur + identifiant). */
const getPageTitle = computed(() =>
    t("title", {
        idtype: currentSource.value?.type,
    })
)

// ================================
// BUSINESS LOGIC
// ================================
/**
 * Détermine la fréquence initiale à utiliser en fonction du type de source
 * et des paramètres de l'URL.
 * @returns La fréquence initiale (raw, daily ou depuis l'URL)
 */
function determineInitialFreq(): Freq {
    const source = currentSource.value
    if (
        source?.type &&
        SOURCE_WITHOUT_RAW_FREQ.includes(source.type) &&
        graphStore.freq === "raw"
    ) {
        return "daily"
    }
    if (
        source?.type &&
        SOURCE_WITHOUT_HOURLY_FREQ.includes(source.type) &&
        graphStore.freq === "hourly"
    ) {
        return "daily"
    }
    if (route.query.freq && typeof route.query.freq === "string") {
        return route.query.freq as Freq
    }
    return "raw"
}

// ================================
// WATCHERS
// ================================
/**
 * Surveille les changements de source et charge la source si nécessaire
 */
watch(
    () => route.params?._id_source,
    async (newId) => {
        if (!newId) return
        const id = newId as string
        fetchInitialData(id)
    },
    { immediate: true }
)

/**
 * Une fois la source et la liste des graphiques chargés,
 * initialise freq et graphStore.graphs (localStorage ou liste par défaut).
 */
watch(
    () => isFetchingInitialData.value,
    (fetching) => {
        if (!fetching && sourceIdParam.value && graphStore.isGraphListLoaded) {
            const source = sourceStore.getSourceById(sourceIdParam.value)
            if (
                source?.informations?.pdl?.etat !== "en_service" &&
                source?.type === SOURCES_TYPES.PDL
            ) {
                return
            }
            graphStore.freq = determineInitialFreq()
            graphStore.updateGraphList(
                graphStore.getOptimalSourceGraph.length > 0
                    ? graphStore.getOptimalSourceGraph
                    : getCardGraphOptionList.value
            )
            fetchKPIValue()
        }
    },
    { immediate: true }
)

// ================================
// INTERACTIONS
// ================================
/**
 * Met à jour la liste des graphiques et recharge les KPI.
 * @param graphs - Liste des graphiques sélectionnés
 */
function onUpdateGraphList(graphs: string[]) {
    graphStore.updateGraphList(graphs)
    fetchKPIValue()
}

/**
 * Ajoute tous les graphiques disponibles au sélecteur
 */
function onClickAddOptimalGraphs() {
    graphStore.updateGraphList(getCardGraphOptionList.value)
    setTimeout(() => {
        fetchKPIValue()
    }, 500)
}

/**
 * Indique si le consentement de la source est invalide ou non
 * Si ce n'est pas un PDL ou PCE alors le consentement est valide.
 */
const isConsentInvalid = computed(() => {
    if (
        ![SOURCES_TYPES.PDL, SOURCES_TYPES.PCE].includes(
            currentSource.value?.type as string
        )
    ) {
        return false
    }
    // Vérifie le consentement dans le cas d'une source de type PDL,PCE
    return (
        !currentSource.value?.consentement ||
        currentSource.value?.consentement?.dismissed ||
        !currentSource.value?.consentement?.signed ||
        new Date(currentSource.value?.consentement?.date_to) < new Date()
    )
})

/**
 * Indique si l'utilisateur posséde le droit own
 */
const isRightOwnsOnly = computed(() => {
    // si le droit owns est le seul présent dans le tableau des droits alors renvoie true
    return currentSource.value?.rights?.every((right) => right === "owns")
})

/**
 * Indique si l'utilisateur possède le droit de télécharger les exports
 */
const hasExport = computed(() => {
    return (
        !isConsentInvalid.value &&
        !isRightOwnsOnly.value &&
        authStore.isSubscriptionActive
    )
})

/**
 * Indique si l'utilisateur possède le droit de recharger les données
 * 1 - Il est sur un abonnement actif EA avec un consentement valide et pas seulement le droit owns
 * 2 - Il a un consentement valide et pas seulement le droit owns
 * et n'est pas sur un abonnement actif EA sur une source qui n'est pas un compteur ou une station météo
 */
const hasRightToReloadData = computed(() => {
    return (
        !isConsentInvalid.value &&
        authStore.isSubscriptionActive &&
        (authStore?.subscription?.plan === "EA" ||
            ![
                SOURCES_TYPES.PDL,
                SOURCES_TYPES.PCE,
                SOURCES_TYPES.OPENMETEO,
                SOURCES_TYPES.METEOFRANCE,
                SOURCES_TYPES.METEO,
            ].includes(currentSource.value?.type as string))
    )
})

/** Contrôle l'affichage de la dialog d'export. */
const showExportDialog = ref(false)

/**
 * Ouvre la dialog d'export des données de la source.
 */
function onClickExport() {
    showExportDialog.value = true
}

/**
 * Lance la collecte on-demand des données pour la source courante.
 *
 * DEV-527 / DEV-16199 — la collecte passe désormais par api-metier
 * (`POST /task/update/source`, routage source→collecteur : un PCE grdf part
 * vers harvest, les autres opérateurs gardent le collecteur legacy). On suit le
 * `task_id` renvoyé (toast + subscribe), au lieu d'enfiler directement le
 * `getlastdata` legacy — qui est **no-opé pour grdf** côté data (le bouton
 * « Recharger » ne faisait donc plus rien sur les compteurs gaz).
 *
 * Mise à jour **incrémentale** (depuis la dernière date + marge) : l'utilisateur
 * n'a pas besoin de lancer un rebuild complet. Les PDL Enedis conservent leur
 * comportement historique (rebuild de l'app depuis les données workers).
 */
async function onClickReload() {
    const id = sourceIdParam.value
    if (!id) return
    const source = currentSource.value
    if (!source) return

    const idKind = (source.idtype ?? source.type) as string
    const rebuild = idKind === SOURCES_TYPES.PDL

    try {
        const { task_id } = await sourceService.triggerSourceDataUpdate(id, {
            rebuild,
        })
        if (!task_id) {
            throw new Error("Aucun task_id renvoyé par la collecte")
        }
        taskStore.followEvent(task_id, task_id, {
            taskname: TASK_REBUILD_SOURCE_DATA,
            autoDelete: true,
            onSuccess: async () => {
                await fetchInitialData(id)
            },
        })
    } catch (error) {
        console.error(
            "Erreur lors du lancement de la tâche de rechargement des données",
            error
        )
    }
}

/**
 * Retire un graphique de la liste affichée.
 * @param graphId - Identifiant du graphique à retirer
 */
function onClickRemove(graphId: string): void {
    onUpdateGraphList(graphStore.graphs.filter((graph) => graph !== graphId))
}

onBeforeUnmount(() => {
    graphStore.cancelPendingGraphRequests()
    graphStore.setCurrentSourceId(null)
})
</script>
<template>
    <div class="page page-source-data">
        <!-- ================================ -->
        <!-- EN-TÊTE -->
        <!-- ================================ -->
        <HeaderPageSource
            :title="getPageTitle"
            :source-id="sourceIdParam"
            class-header="flex justify-between items-center flex-wrap gap-4"
        >
            <div class="page__header-action flex items-center gap-2">
                <BaseButton
                    v-if="hasExport"
                    :label="t('export')"
                    size="small"
                    icon="pi pi-download"
                    :disabled="isFetchingInitialData"
                    @click="onClickExport"
                />
                <BaseButton
                    v-if="hasRightToReloadData && !isFetchingInitialData"
                    icon="pi pi-sync"
                    size="small"
                    severity="secondary"
                    v-tooltip.left="t('reload')"
                    rounded
                    :aria-label="t('reload')"
                    :disabled="isFetchingInitialData"
                    @click="onClickReload"
                />
            </div>
        </HeaderPageSource>

        <ExportSourceDialog
            v-if="sourceIdParam"
            v-model:visible="showExportDialog"
            :_id_source="sourceIdParam"
        />

        <template v-if="isFetchingInitialData">
            <BaseSkeleton width="100%" height="3rem" />
            <BaseSkeleton width="100%" height="40rem" />
            <BaseSkeleton width="100%" height="40rem" />
        </template>

        <!-- ================================ -->
        <!-- CONTENU PRINCIPAL -->
        <!-- ================================ -->
        <template v-else>
            <!-- Message important en haut -->
            <BaseAppMessage
                v-if="getGlobalMessage?.position === 'top'"
                v-bind="getGlobalMessage"
            />

            <!-- ============================================ -->
            <!-- GRDF gaz (PCE) : page de données BRUTES dédiée (DEV-595) -->
            <!-- ============================================ -->
            <template v-if="isGrdfGas">
                <!-- Skeleton uniquement au 1er chargement (aucune donnée encore). -->
                <BaseSkeleton
                    v-if="grdfLoading && !grdfSources.length"
                    width="100%"
                    height="40rem"
                />
                <!-- Erreur seulement si on n'a jamais rien pu afficher. -->
                <div
                    v-else-if="grdfError && !grdfSources.length"
                    class="py-8 text-center text-red-500"
                >
                    {{ t("grdf_error") }}
                </div>
                <!-- Sinon : composant MONTÉ en continu (overlay pendant refetch)
                     → l'état (vue, sélecteurs, toggle) persiste entre périodes. -->
                <SourceDataGrdfView
                    v-else
                    :pce-number="
                        formatIdSource(currentSource?.idsource ?? '', 'pce')
                    "
                    :period="graphStore.period"
                    :sources="grdfSources"
                    :loading="grdfLoading"
                    @update:period="onGrdfPeriod"
                />
            </template>

            <!-- ============================================ -->
            <!-- Cas générique (autres types de source) -->
            <!-- ============================================ -->
            <template v-else>
                <!-- En-tête filtres (fréquence, période, graphiques) -->
                <DataHeader
                    :option-graphs="getCardGraphOptionList"
                    v-model:freq="graphStore.freq"
                    v-model:graphs="graphStore.graphs"
                    v-model:period="graphStore.period"
                    :freqs="getAvailableFreqs"
                    @update:graphs="onUpdateGraphList"
                    @update:period="fetchKPIValue"
                    @update:freq="fetchKPIValue"
                />
                <!-- Indicateurs KPI -->
                <DataIndicatorList
                    v-if="indicators.length > 0"
                    :indicators="indicators"
                    :error="errorKPI"
                    :loading="loadingKPI"
                    @click:kpi="onClickKPI"
                />
                <!-- Liste des graphiques -->
                <DataChartListItem
                    v-for="{ graphId, graphHidden } of getGraphSelected"
                    :key="graphId"
                    :graph-id="graphId"
                    type="source"
                    :source="currentSource"
                    :graph-hidden="graphHidden"
                    @click:remove="onClickRemove(graphId)"
                />
            </template>

            <!-- Message important en bas -->
            <BaseAppMessage
                v-if="getGlobalMessage?.position === 'bottom'"
                v-bind="getGlobalMessage"
            />
        </template>
    </div>
</template>
